/**
 * Discount Calculation Module
 *
 * This module provides comprehensive discount calculation utilities for the OrderWire POS system.
 * It handles various discount types (percentage, fixed amount, buy-X-get-Y promotions) at both
 * order and order-line levels, including comp (complimentary) items and complex distribution
 * scenarios across multiple order lines.
 *
 * Key Features:
 * - Support for multiple discount types (percentage, fixed amount, buy-X-get-Y)
 * - Order-level and line-item-level discount calculations
 * - Discount distribution across multiple order lines
 * - Comp item handling with configurable calculation timing
 * - Precise decimal arithmetic to prevent rounding errors
 */

import Decimal from 'decimal.js'
import {
    DiscountContract,
    VoidCompReasonModel,
    DiscountType,
    DiscountGetDiscountType,
    DiscountLevel,
} from 'models'

import { mockDiscountForComp } from 'views/Sale/discount/mockDiscountForComp'
import {
    adjustSplitedValue,
    BaseOrderItem,
    calculateLineSubtotal,
    ceil,
    round,
} from './common'
import { sortBy } from 'lodash-es'
import { sumDecimal } from 'utilities/decimalsManipulate'
import { LineModelExtend } from './models/IOrderCalculationModel'
import { sortDiscountTypeOfDiscount } from 'views/Sale/discount/discountMapper/discountSortting'

/**
 * Represents a discount calculation result with the discount details and calculated amount.
 */
export interface IDiscountResult {
    /** The discount contract containing discount rules and configuration */
    discount: DiscountContract
    /** The calculated discount amount in currency */
    amount: Decimal
}

/**
 * Calculates discount amounts for a list of discounts applied to a subtotal.
 *
 * This function processes discounts sequentially, accumulating their effects
 * to prevent overlapping discounts from exceeding the available subtotal.
 * Percentage discounts are calculated on the original subtotal, while fixed
 * amount discounts are calculated on the remaining subtotal after previous
 * percentage discounts.
 *
 * @param maxSubtotal - The maximum subtotal amount available for discounting
 * @param discounts - Array of discount contracts to calculate
 * @param itemAmount - Number of items (used for line-item discounts). Default is 1
 * @returns Array of discount results with amounts for each discount
 *
 * @example
 * const discounts = [
 *   { type: DiscountType.Percentage, value: 10 },
 *   { type: DiscountType.FixedAmount, value: 5 }
 * ];
 * const results = calculateDiscounts(new Decimal(100), discounts);
 * // results[0].amount = 10 (10% of 100)
 * // results[1].amount = 5 (fixed 5 on remaining amount)
 */
export const calculateDiscounts = (
    maxSubtotal: Decimal,
    discounts: DiscountContract[],
    itemAmount: number = 1,
): IDiscountResult[] => {
    let totalDiscount = new Decimal(0)
    return discounts.map((d) => {
        const expectDiscount = calculateDiscountAmount(
            d,
            d.type === DiscountType.Percentage
                ? maxSubtotal
                : maxSubtotal.sub(totalDiscount),
            itemAmount,
        )
        const discountAmount = Decimal.min(maxSubtotal, expectDiscount)
        totalDiscount = totalDiscount.plus(discountAmount)
        return {
            discount: d,
            amount: discountAmount,
        }
    })
}
/**
 * Calculates the total discount amount and individual discount breakdowns.
 *
 * Supports both order-level and order-line-level discount calculations.
 * Handles negative subtotals gracefully by returning zero discount amounts.
 *
 * @param maxSubtotal - The base subtotal amount to calculate discounts against
 * @param discounts - Array of discount contracts to apply
 * @param itemAmount - Number of items being discounted. Default is 1
 * @returns Object containing:
 *   - totalDiscountAmount: The sum of all applicable discounts, capped at maxSubtotal
 *   - discountResult: Array of individual discount results
 *
 * @example
 * const result = calculateTotalDiscountsAmount(
 *   new Decimal(100),
 *   [{ type: DiscountType.Percentage, value: 10 }]
 * );
 * // result.totalDiscountAmount = 10
 * // result.discountResult[0].amount = 10
 */
export const calculateTotalDiscountsAmount = (
    maxSubtotal: Decimal,
    discounts: DiscountContract[],
    itemAmount: number = 1,
): {
    totalDiscountAmount: Decimal
    discountResult: IDiscountResult[]
} => {
    if (maxSubtotal.lessThan(0))
        return {
            totalDiscountAmount: new Decimal(0),
            discountResult: discounts.map((a) => ({
                discount: a,
                amount: new Decimal(0),
            })),
        }

    const discountResults = calculateDiscounts(
        maxSubtotal,
        discounts,
        itemAmount,
    )
    const totalDiscount = sumDecimal(discountResults.map((a) => a.amount))

    return {
        totalDiscountAmount: Decimal.min(totalDiscount, maxSubtotal),
        discountResult: discountResults,
    }
}

/**
 * Calculates an order line's subtotal after applying item-level discounts.
 *
 * This function computes the final price of a line item after applying
 * all item-level discounts, but does not include order-level discounts.
 *
 * @param item - The order item containing quantity and pricing information
 * @param discounts - Array of item-level discount contracts to apply
 * @returns The line subtotal after applying all discounts
 *
 * @example
 * const item = { price: new Decimal(50), amount: 2 };
 * const discounts = [{ type: DiscountType.Percentage, value: 10 }];
 * const result = calculateLineSubTotalAfterDiscounts(item, discounts);
 * // result = 90 (100 - 10% discount)
 */
export const calculateLineSubTotalAfterDiscounts = (
    item: BaseOrderItem,
    discounts: DiscountContract[],
) => {
    const lineSubtotal = calculateLineSubtotal(item)

    const { totalDiscountAmount: discountAmount } =
        calculateTotalDiscountsAmount(lineSubtotal, discounts, item.amount)
    return lineSubtotal.sub(discountAmount)
}

/**
 * Distributes order-level discounts across multiple order lines.
 *
 * This function handles complex scenarios where order-level discounts and comps
 * need to be fairly distributed among multiple order lines. It:
 * - Sorts lines by their discounted totals and comp amounts
 * - Applies discounts sequentially in the correct order
 * - Handles comp (complimentary) items with configurable timing
 * - Maintains sorting indices for API consistency
 *
 * @param itemLines - Array of order line models to distribute discounts across
 * @param entireDiscounts - Array of order-level discount contracts
 * @param entireComp - Optional order-level comp (complimentary) configuration
 * @param isCalculateCompAfterDiscount - If true, comp is calculated after other discounts;
 *                                       if false, comp is calculated on original subtotal
 * @returns The updated item lines with distributed discount amounts
 *
 * @remarks
 * The function modifies the orderDiscountAmount property of each line.
 * The sorting order matches the backend API implementation for consistency.
 */
export const splitOrderDiscountsToLines = (
    itemLines: LineModelExtend[],
    entireDiscounts: DiscountContract[],
    entireComp: VoidCompReasonModel | undefined,
    isCalculateCompAfterDiscount: boolean,
) => {
    //reset order discount in each line
    itemLines.forEach((line) => {
        line.orderDiscountAmount = new Decimal(0)
    })
    sortingLine()
    const maximumDiscount = sumDecimal(
        itemLines.map((a) =>
            a.subtotal
                .minus(a.itemDiscountAmount ?? 0)
                .minus(a.lineCompDiscount ?? 0),
        ),
    )
    const sortedDiscounts = sortDiscountTypeOfDiscount(entireDiscounts)
    sortedDiscounts.forEach((discount) => {
        distributeDiscountValueToLines(
            discount,
            maximumDiscount,
            itemLines,
            false,
        )
    })

    const totalDiscountAmount = sumDecimal(
        itemLines.map((l) => l.orderDiscountAmount),
    )

    if (entireComp) {
        distributeDiscountValueToLines(
            {
                level: DiscountLevel.EntireOrder,
                type: entireComp.isPercentage
                    ? DiscountType.Percentage
                    : DiscountType.FixedAmount,
                value: entireComp.value ?? 0,
            },
            isCalculateCompAfterDiscount
                ? maximumDiscount.sub(totalDiscountAmount)
                : maximumDiscount,
            itemLines,
            isCalculateCompAfterDiscount,
        )
    }

    return itemLines

    /**
     * Internal function: Sorts order lines by their net subtotal (after comps and item discounts)
     * in descending order, then reassigns sorting indices to maintain API consistency.
     */
    function sortingLine() {
        itemLines = sortBy(itemLines, [
            (l) =>
                (l.lineTotalWithItemDiscount ?? 0) - (l.lineCompDiscount ?? 0),
            (l) => l.sortingIndex,
        ])
        itemLines.reverse()

        itemLines = itemLines.map((l, i) => ({ ...l, sortingIndex: i }))
    }
}

/**
 * Calculates the total discount amount for an entire order.
 *
 * This function applies order-level discounts and optionally adds comp
 * (complimentary) items. It handles the timing of comp calculations
 * (before or after other discounts) based on configuration.
 *
 * @param subtotal - The order's subtotal before discounts
 * @param entireDiscounts - Array of order-level discount contracts
 * @param entireComp - Optional order-level comp (complimentary) configuration
 * @param isCalculateCompAfterDiscount - If true, comp is applied to the remaining amount
 *                                       after order discounts. If false, comp is applied
 *                                       to the original subtotal
 * @returns The total discount amount (order discounts + comp discount) in currency
 *
 * @example
 * // Discount first, then apply comp on remaining
 * const totalDiscount = calculateTotalEntireDiscount(
 *   new Decimal(100),
 *   [{ type: DiscountType.Percentage, value: 10 }],
 *   { isPercentage: true, value: 5 },
 *   true
 * );
 * // Order discount: 10, Comp on remaining 90: 4.5, Total: 14.5
 */
export function calculateTotalEntireDiscount(
    subtotal: Decimal,
    entireDiscounts: DiscountContract[],
    entireComp: VoidCompReasonModel | undefined,
    isCalculateCompAfterDiscount: boolean,
) {
    const { totalDiscountAmount: totalEntireDiscount } =
        calculateTotalDiscountsAmount(subtotal, entireDiscounts)
    if (entireComp) {
        const maxTotal = isCalculateCompAfterDiscount
            ? Decimal.max(new Decimal(0), subtotal.sub(totalEntireDiscount))
            : subtotal
        const compDiscount = mockDiscountForComp(entireComp, true, maxTotal)
        return totalEntireDiscount.plus(compDiscount.value ?? 0)
    }
    return totalEntireDiscount
}
/**
 * Internal interface representing a single order line during discount distribution.
 *
 * Used internally to track line-level discount calculations and results.
 */
interface IDistributeLine {
    /** Unique identifier for the order item */
    id: string
    /** The line's subtotal available for discount (after excluding taxes, comps, and item-level discounts) */
    subtotal: Decimal
    /** The calculated discount amount to be applied to this line */
    splitedResult: Decimal
    /** Discount amount already applied at the order level */
    extraDiscount: Decimal
}

/**
 * Distributes a single discount across multiple order lines based on their subtotals.
 *
 * For fixed-amount discounts, distribution is based on the proportion of each line's
 * subtotal relative to the total. For percentage discounts, each line receives the
 * discount percentage applied to its own subtotal.
 *
 * The function modifies the orderDiscountAmount property of each line in-place to
 * accumulate the distributed discount.
 *
 * @param discount - The discount contract to distribute
 * @param maximumSumDiscount - The maximum total amount available for discounting
 * @param itemLines - Array of order lines to distribute the discount across.
 *                    Must have orderDiscountAmount property initialized as Decimal
 * @param isCalculationAfterOrderDiscount - If true, percentage discounts are applied to the
 *                                          line's subtotal minus existing order discounts.
 *                                          If false, applied to full line subtotal.
 *
 * @remarks
 * This function modifies itemLines in-place. It uses ceiling operations to ensure
 * rounding errors don't result in lost cents, then adjusts the final line to reconcile.
 *
 * @example
 * // Distribute 10% discount across two lines ($50 and $30 each)
 * const lines = [
 *   { orderItemId: '1', subtotal: new Decimal(50), orderDiscountAmount: new Decimal(0), ... },
 *   { orderItemId: '2', subtotal: new Decimal(30), orderDiscountAmount: new Decimal(0), ... }
 * ];
 * const discount = { type: DiscountType.Percentage, value: 10 };
 * distributeDiscountValueToLines(discount, new Decimal(80), lines, false);
 * // lines[0].orderDiscountAmount = 5 (10% of 50)
 * // lines[1].orderDiscountAmount = 3 (10% of 30)
 */
export function distributeDiscountValueToLines(
    discount: DiscountContract,
    maximumSumDiscount: Decimal,
    itemLines: Pick<
        LineModelExtend,
        | 'orderItemId'
        | 'subtotal'
        | 'excludeFromItemTaxAmount'
        | 'lineCompDiscount'
        | 'itemDiscountAmount'
        | 'orderDiscountAmount'
    >[],
    isCalculationAfterOrderDiscount: boolean,
) {
    const splitedLines = itemLines.map<IDistributeLine>((a) => {
        return {
            id: a.orderItemId,
            subtotal: a.subtotal
                .sub(a.lineCompDiscount ?? 0)
                .sub(a.itemDiscountAmount ?? 0),
            extraDiscount: a.orderDiscountAmount,
            splitedResult: new Decimal(0),
        }
    })

    const expectedDiscountAmount = calculateDiscountAmount(
        discount,
        maximumSumDiscount,
    )

    const splitLineDiscounts = splitedLines.map((line) => {
        let lineDiscountAmount = new Decimal(0)
        if (discount.type === DiscountType.FixedAmount) {
            const orderLineRatio = line.subtotal.equals(0)
                ? new Decimal(0)
                : ceil(line.subtotal.div(maximumSumDiscount))
            lineDiscountAmount = expectedDiscountAmount.mul(orderLineRatio)
        } else {
            const lineNetSubtotal = line.subtotal.sub(
                isCalculationAfterOrderDiscount ? line.extraDiscount : 0,
            )
            lineDiscountAmount = calculateDiscountAmount(
                discount,
                lineNetSubtotal,
            )
        }
        return Decimal.min(
            line.subtotal.sub(line.extraDiscount),
            ceil(lineDiscountAmount),
        )
    })
    adjustSplitedValue(splitLineDiscounts, expectedDiscountAmount)
    splitedLines.forEach((l, index) => {
        l.splitedResult = l.splitedResult.plus(splitLineDiscounts[index])
    })

    splitedLines.forEach((r) => {
        const line = itemLines.find((a) => a.orderItemId === r.id)!
        line.orderDiscountAmount = line.orderDiscountAmount.plus(
            r.splitedResult,
        )
    })
}

/**
 * Calculates the discount amount for a percentage-based discount.
 *
 * Performs the calculation: (subtotal * percent) / 100
 * Uses rounding to ensure proper decimal precision.
 *
 * @param percent - The percentage to calculate (e.g., 10 for 10%)
 * @param subtotal - The base amount to calculate the percentage on
 * @returns The calculated discount amount
 *
 * @example
 * const discount = calculateDiscountPercentageAmount(15, new Decimal(100));
 * // discount = 15
 */
const calculateDiscountPercentageAmount = (
    percent: number,
    subtotal: Decimal,
) => {
    return round(subtotal.mul(percent).div(100))
}

/**
 * Calculates the final discount amount to apply based on discount type and configuration.
 *
 * Handles multiple discount types:
 * - Buy-X-Get-Y with free items: returns the full item price as discount
 * - Percentage discounts: returns percentage of the discountable amount
 * - Fixed amount discounts: returns the fixed value
 *
 * For line-item level fixed amount discounts, multiplies by item quantity.
 * Ensures the discount amount never exceeds the maximum discountable amount.
 *
 * @param discount - The discount contract with type and value configuration
 * @param maxDiscountable - The maximum amount available to be discounted (line price * amount or order subtotal)
 * @param itemAmount - Number of items (used for multiplying line-item level fixed discounts). Default is 1
 * @returns The final discount amount to apply, never exceeding maxDiscountable
 *
 * @example
 * // 10% discount on $100
 * const amount1 = calculateDiscountAmount(
 *   { type: DiscountType.Percentage, value: 10 },
 *   new Decimal(100)
 * );
 * // amount1 = 10
 *
 * // Fixed $5 discount on 3 items
 * const amount2 = calculateDiscountAmount(
 *   { type: DiscountType.FixedAmount, value: 5, level: DiscountLevel.Item },
 *   new Decimal(50),
 *   3
 * );
 * // amount2 = 15 (5 * 3 items)
 */
export const calculateDiscountAmount = (
    discount: DiscountContract,
    /** line.price * amount */
    maxDiscountable: Decimal,
    /** require for order line case */
    itemAmount: number = 1,
) => {
    if (
        discount.type === DiscountType.BuyXGetY &&
        discount.getAtDiscountType === DiscountGetDiscountType.Free
    )
        return maxDiscountable
    let discountPrice = getDiscountValue(discount, maxDiscountable)
    if (
        discount.level === DiscountLevel.Item &&
        (discount.type === DiscountType.FixedAmount ||
            discount.getAtDiscountType === DiscountGetDiscountType.FixedAmount)
    ) {
        discountPrice = discountPrice.mul(itemAmount)
    }

    return Decimal.min(discountPrice, maxDiscountable)
}

/**
 * Extracts the numeric discount value from a discount contract.
 *
 * Handles the distinction between primary and promotional discount types
 * in buy-X-get-Y promotions.
 *
 * @param discount - The discount contract to extract the value from
 * @param subtotal - The subtotal (used for percentage calculations)
 * @returns The calculated discount value as a Decimal
 *
 * @remarks
 * For percentage discounts, this calls calculateDiscountPercentageAmount.
 * For fixed amounts, returns the discount.value property directly.
 */
const getDiscountValue = (discount: DiscountContract, subtotal: Decimal) => {
    return discount.type === DiscountType.Percentage ||
        discount.getAtDiscountType === DiscountGetDiscountType.Percentage
        ? calculateDiscountPercentageAmount(discount.value!!, subtotal)
        : new Decimal(discount?.value ?? 0)
}
